import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';

import { ShowDispositionDialog } from '@/features/show-disposition';
import { MANUAL_NODES } from '@/shared/config/manual';
import { DataTable } from '@/shared/ui/data-table';

import { useReceiveLosses } from '../model';
import { getReceiveLossesColumns, getReceiveLossesColumnPresets, buildReceiveLossesRowActions } from '../model';

export function ReceiveLossesPage() {
  const { t } = useTranslation();
  const [dispositionConsignmentId, setDispositionConsignmentId] = useState<string | null>(null);
  const {
    tableData,
    totalElements,
    isLoading,
    isFetchingNextPage,
    error,
    isValid,
    hasMore,
    handleLoadMore,
    sortBy,
    sortDirection,
    handleSort
  } = useReceiveLosses();

  const columns = useMemo(() => getReceiveLossesColumns(t), [t]);
  const columnPresets = useMemo(() => getReceiveLossesColumnPresets(t), [t]);
  const rowActions = useMemo(
    () => buildReceiveLossesRowActions({ onShowDisposition: setDispositionConsignmentId, t }),
    [t]
  );

  if (!isValid) {
    return (
      <Alert>
        <AlertDescription>
          {t('receive.error.invalidParams', 'Invalid project or site parameters. Please check the URL.')}
        </AlertDescription>
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {t('receive.losses.error.loadFailed', 'Failed to load the lost or not sent items. Please try again later.')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold text-foreground">
          {t('receive.losses.title', 'Lost or Not Sent Items')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {totalElements} {t('gen.numres.label', 'results found')}
        </p>
      </header>

      <DataTable
        data={tableData}
        columns={columns}
        title={t('receive.losses.title', 'Lost or Not Sent Items')}
        helpTarget={MANUAL_NODES.RECEIVE_LOSSES}
        tableId="receive-losses-table"
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        sortField={sortBy}
        sortDirection={sortDirection}
        onSort={handleSort}
        actions={rowActions}
        enableFullscreen
        enableColumnCustomization
        columnPresets={columnPresets}
      />

      <ShowDispositionDialog
        open={!!dispositionConsignmentId}
        onOpenChange={(open) => {
          if (!open) setDispositionConsignmentId(null);
        }}
        consignmentId={dispositionConsignmentId}
      />
    </div>
  );
}
